/* eslint-disable react-hooks/exhaustive-deps */
// selectDropdownPatch.js
import React, { useEffect } from "react";
import { Select } from "react-materialize";

/** คลาสสำหรับหัวข้อ (option ที่ disabled) */
export const DEFAULT_LABEL_CLASS = "option-label";

/**
 * แพตช์ <ul class="select-dropdown"> ของ materialize:
 * - ใส่คลาสให้แถว label (li.disabled)
 * - ล้าง .active ที่ materialize ใส่ให้ตอนเปิด เพื่อกันพื้นหลังเข้ม
 */
export function patchSelectUlById(selectId, opts = {}) {
  const { labelClass = DEFAULT_LABEL_CLASS, clearActive = true } = opts;

  const sel = document.getElementById(selectId);
  if (!sel) return;

  const wrapper = sel.closest(".select-wrapper") || sel.parentElement;
  if (!wrapper) return;

  const ul = wrapper.querySelector("ul.select-dropdown");
  if (!ul) return;

  // ✅ ใส่คลาสให้หัวข้อ (option disabled) และลบ active ออก
  ul.querySelectorAll("li.disabled").forEach((li) => {
    const text = li.innerText.trim().toLowerCase();

    if (text === "division" || text === "department") {
      // 👉 เป็น placeholder
      li.classList.add("option-placeholder");
      li.classList.remove(labelClass); // กันสับสนกับหัวข้อ
    } else {
      // 👉 เป็นหัวข้อ label
      li.classList.add(labelClass);
    }

    li.classList.remove("active"); // กันพื้นหลังเขียว
  });

  if (clearActive) {
    // ล้าง active ของ li ที่ไม่ควรติด
    ul.querySelectorAll("li.active").forEach((li) => {
      if (
        li.classList.contains(labelClass) ||
        li.classList.contains("option-placeholder") ||
        li.classList.contains("disabled")
      ) {
        li.classList.remove("active");
      }
    });
  }
}

/**
 * Hook: ผูก event ตอนเปิดดรอปดาวน์ เพื่อเรียก patch ทุกครั้ง
 * ใช้ id ของ <Select> เป็นตัวอ้างอิง
 */
export function useSelectDropdownPatch(selectId, opts = {}) {
  useEffect(() => {
    if (!selectId) return;
    const sel = document.getElementById(selectId);
    if (!sel) return;

    const wrapper = sel.closest(".select-wrapper") || sel.parentElement;
    if (!wrapper) return;

    const runPatch = () =>
      requestAnimationFrame(() => patchSelectUlById(selectId, opts));

    // bind event กับ input.select-dropdown (ตัวที่ materialize สร้าง)
    const bind = () => {
      const input = wrapper.querySelector("input.select-dropdown");
      if (!input) return null;
      input.addEventListener("click", runPatch);
      input.addEventListener("focus", runPatch);
      input.addEventListener("keydown", runPatch); // รองรับเปิดด้วยคียบอร์ด
      return () => {
        input.removeEventListener("click", runPatch);
        input.removeEventListener("focus", runPatch);
        input.removeEventListener("keydown", runPatch);
      };
    };

    // ผูกครั้งแรก (ถ้า input มาไม่ทัน จะมี MO ช่วย)
    let unbind = bind();

    // เฝ้าดู DOM เผื่อ materialize re-init แล้ว input เปลี่ยนใหม่
    const mo = new MutationObserver(() => {
      if (unbind) unbind();
      unbind = bind();
    });
    mo.observe(wrapper, { childList: true, subtree: true });

    // ยิงแพตช์รอบแรกหลัง mount
    runPatch();

    return () => {
      mo.disconnect();
      if (unbind) unbind();
    };
  }, [selectId]); // พอแล้ว ผูกกับ id เดียว
}

/**
 * คอมโพเนนต์หุ้ม Select: ใช้แทน <Select> เดิมได้ทันที
 * - เรียก useSelectDropdownPatch() ให้อัตโนมัติ
 * - เติม onOpenStart เพื่อย้ำ patch อีกรอบตอนจะเปิด
 */
export function PatchedSelect({
  id,
  options,
  patchOptions, // { labelClass?: string, clearActive?: boolean }
  children,
  ...rest
}) {
  useSelectDropdownPatch(id, patchOptions);

  const mergedOptions = {
    ...(options || {}),
    dropdownOptions: {
      ...(options?.dropdownOptions || {}),
      // ย้ำแพตช์รอบเปิด (บางธีม render ช้า)
      onOpenStart: () => patchSelectUlById(id, patchOptions) } };

  return (
    <Select id={id} options={mergedOptions} {...rest}>
      {children}
    </Select>
  );
}

